import { string, object, array, number, boolean, date } from 'yup';
import moment from 'moment';
import { get } from 'lodash/fp';
import { IntlShape } from 'react-intl';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module 'goog... Remove this comment to see the full error message
import { PhoneNumberUtil } from 'google-libphonenumber';
import { ReactStateSetter } from '@whitelabel/helpers/types';
import messages from '@whitelabel/helpers/forms/messages';
import { getQuote } from '../bookings';
import { FORM_INPUT_TYPES } from '../constants';
import { ISectionField } from '../types/fnolTypes';
import { IBooking } from '../types';

function allowEmptyString(value: any, originalValue: any) {
  // @ts-ignore ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
  if (this.isType(value)) return value;
  if (!originalValue || !originalValue.trim()) return undefined;
  return value;
}

const formattedDate = (format: any) =>
  function transformDate(value: any, originalValue: any) {
    // @ts-ignore ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
    if (this.isType(value)) return value;
    const newValue = moment(originalValue, format);
    return newValue.isValid() ? newValue.toDate() : value;
  };

const fnolFormattedDate = formattedDate(['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']);

// Takes field type and returns Yup schema for FNOL
export const getFNOLSchemaType = (type: string, name: string, intl: IntlShape, display: string) => {
  switch (type) {
    case 'email':
      return string().trim().lowercase().email();

    case 'numeric':
      return number().transform(allowEmptyString);

    case 'url':
      return string().trim().url();

    case 'date':
      if (display === 'dob') {
        return getDobSchema(intl, false);
      } else return date().transform(allowEmptyString).transform(fnolFormattedDate);

    case 'datetime':
      return date().transform(allowEmptyString).transform(fnolFormattedDate);

    case 'boolean':
      return boolean();

    case 'geolocation':
      return object({
        longitude: number(),
        lattitude: number(),
      });

    case 'set':
    case 'file':
      return array(string().required());

    // case 'string' has been replaced by case 'phone'
    // leave this case for transition,
    // todo: remove case 'string' in the future
    case 'string':
      if (name === 'phone') {
        return getPhoneSchema(intl, false);
      }
      return string().trim();

    case 'phone':
      return getPhoneSchema(intl, false);

    default:
      return string().trim();
  }
};

export const getQuoteFormSchemaType = (type: any, intl: any) => {
  switch (type) {
    case 'number':
      return number().transform(allowEmptyString);

    case 'datepicker':
      return date().transform(allowEmptyString);

    case 'country_select':
      return string();

    case FORM_INPUT_TYPES.ADDRESS:
      return object({
        region: string().required(intl.formatMessage(messages.regionRequired)),
        postcode: string().required(intl.formatMessage(messages.postcodeRequired)),
      });

    default:
      return string().trim();
  }
};

// Returns Yup schema with field validation rules based FNOL field and quote to be claimed against
export const getSchemaValidation = (schema: any, field: any, quoteID: any, booking: any) => {
  const { type, name, validation, required } = field;
  const integer = type === 'numeric' && !get('float', validation);
  const min = get('length.min', validation) || get('numeric.min', validation) || get('multiselect.min', validation);
  const max = get('length.max', validation) || get('numeric.max', validation) || get('multiselect.max', validation);
  let validatedSchema = schema;

  if (required) {
    // File upload fields need min 1 validation for required validation to work
    if (type === 'file') validatedSchema = validatedSchema.min(1);
    validatedSchema = validatedSchema.required();
  }

  if (integer) validatedSchema = validatedSchema.integer();
  if (min) validatedSchema = validatedSchema.min(min);
  if (max) validatedSchema = validatedSchema.max(max);

  if (name === 'loss_at') {
    const quote = getQuote(quoteID, booking);
    const confirmedAtDate = moment(quote.confirmedAt);
    const minDate = confirmedAtDate.startOf('day').toISOString();
    /**
     * user shouldn't be able to enter a date after today.
     * allowed dates are between policy confirmed date and today.
     * enable the current date if the claim is made on the same day as confirmedAt.
     *  */
    const maxDate = moment().endOf('day').toISOString();
    validatedSchema = validatedSchema.min(minDate).max(maxDate);
  }

  return validatedSchema;
};

// Returns Yup field schema based on getFNOL field data
export const getFieldSchema = (field: any, quoteID: any, booking: any, intl: any) => {
  let schema = getFNOLSchemaType(field.type, field.name, intl, field.meta.display).label(field.meta.label);
  schema = getSchemaValidation(schema, field, quoteID, booking);
  return schema;
};

export const getFNOLFieldsSchema = (fields: ISectionField[], quoteID: string, booking: IBooking, intl: IntlShape) =>
  fields.reduce((fieldsObj: { [fieldName: string]: any }, field: ISectionField) => {
    fieldsObj[field.name] = getFieldSchema(field, quoteID, booking, intl);
    return fieldsObj;
  }, {});

export const getQuestionSchema = (
  { name, input, validation: { min, max, required, regex } }: any,
  intl: any,
  setGlobalError: ReactStateSetter<null | unknown>,
) => {
  const locationErrorMessage = intl.formatMessage(messages.isSupportedLocation);

  let schema = getQuoteFormSchemaType(input, intl).label(name);

  if (required) {
    schema = schema.required();
  }

  if (name === 'post_code') {
    // @ts-ignore ts-migrate(2554) FIXME: Expected 1 arguments, but got 3.
    schema = schema.test('postcode', locationErrorMessage, (value: any) => {
      if (value !== undefined) {
        if (!value.match(regex) || (min && value.length < min) || (max && value.length > max)) {
          setGlobalError(locationErrorMessage);
          return false;
        }
      }
      setGlobalError(null);
      return true;
    });
  } else if (name === 'country') {
    // @ts-ignore ts-migrate(2554) FIXME: Expected 1 arguments, but got 3.
    schema = schema.test('country', locationErrorMessage, (value: any) => {
      if (value !== 'AU') {
        setGlobalError(locationErrorMessage);
        return false;
      }
      setGlobalError(null);
      return true;
    });
  } else if (input === FORM_INPUT_TYPES.ADDRESS) {
    // @ts-ignore ts-migrate(2554) FIXME: Expected 1 arguments, but got 3.
    schema = schema.test(name, ' ', (value: any) => {
      if (value.country !== 'AU') {
        setGlobalError(locationErrorMessage);
        return false;
      }
      setGlobalError(null);
      return true;
    });
  } else {
    // @ts-ignore ts-migrate(2339) FIXME: Property 'min' does not exist on type 'StringSchem... Remove this comment to see the full error message
    if (min) schema = schema.min(min);
    // @ts-ignore ts-migrate(2339) FIXME: Property 'max' does not exist on type 'StringSchem... Remove this comment to see the full error message
    if (max) schema = schema.max(max);
  }

  return schema;
};

export const getPasswordSchema = (intl: any) => {
  const min = 8;
  const max = 99;
  return string()
    .required()
    .min(min)
    .max(max)
    .matches(/[\d+]+/, intl.formatMessage(messages.containsNumber))
    .matches(/[a-zA-Z]+/, intl.formatMessage(messages.containsAlpha))
    .matches(/^[\S]+.*[\S]+$/, intl.formatMessage(messages.noSpaceAtStartAndEnd));
};

export const getEmailSchema = (required = true) => {
  let schema = string().trim();
  if (required) schema = schema.required();
  return schema.lowercase().email();
};

export const getPhoneSchema = (intl: any, required = true) => {
  let schema = string().trim();
  if (required) schema = schema.required();
  return schema.test('phone', intl.formatMessage(messages.isPhone), (value) => {
    if (value) {
      try {
        const phoneNumberUtil = PhoneNumberUtil.getInstance();
        return phoneNumberUtil.isValidNumber(phoneNumberUtil.parse(value));
      } catch {
        return false;
      }
    }
    return true;
  });
};

const isDDMMYYYYFormat = (value: any) => {
  if (value) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return false;
    }
  }

  return true;
};

const isDateElementValid = (value: any, el: any) => {
  if (value) {
    const dateMoment = moment(value, 'DD/MM/YYYY');
    if (!dateMoment.isValid() && dateMoment.invalidAt() === el) {
      return false;
    }
  }

  return true;
};

export const getDobSchema = (intl: any, required = true) => {
  let schema = string();
  if (required) schema = schema.required();
  const maxAge = 125;
  return schema
    .test('dobFormat', intl.formatMessage(messages.isDateGeneric), (value) => {
      if (value) return isDDMMYYYYFormat(value);
      return true;
    })
    .test('dobYear', intl.formatMessage(messages.isDateYear), (value) => {
      if (value) return isDateElementValid(value, 0);
      return true;
    })
    .test('dobMonth', intl.formatMessage(messages.isDateMonth), (value) => {
      if (value) return isDateElementValid(value, 1);
      return true;
    })
    .test('dobday', intl.formatMessage(messages.isDateDay), (value) => {
      if (value) return isDateElementValid(value, 2);
      return true;
    })
    .test('dobMaxAge', intl.formatMessage(messages.dobMaxAge), (value) => {
      if (value) return moment().diff(moment(value, 'DD/MM/YYYY'), 'years') <= maxAge;
      return true;
    })
    .test('dobMaxAge', intl.formatMessage(messages.dobMaxAge), (value) => {
      if (value) return moment().diff(moment(value, 'DD/MM/YYYY'), 'days') >= 1;
      return true;
    });
};
